/**
 * TIMELINE OPÉRATIONNELLE — géométrie et composition, sans React ni DOM.
 *
 * Tout ce qui décide OÙ un élément se pose vit ici, en fonctions pures :
 * c'est ce qui rend la position temporelle testable — un événement à 00:10
 * et un événement à 03:40 doivent être éloignés PROPORTIONNELLEMENT, pas
 * espacés uniformément comme dans une liste.
 */

export interface TlBounds { startMs: number; endMs: number }

export const parseBounds = (b: { start: string; end: string }): TlBounds => ({
  startMs: new Date(b.start).getTime(), endMs: new Date(b.end).getTime(),
})

/** Position 0..1 sur l'axe. Un instant hors bornes est RABATTU au bord —
 *  visible à l'extrémité plutôt que dessiné hors du cadre ou jeté. */
export function fracOf(tMs: number, b: TlBounds): number {
  const span = Math.max(b.endMs - b.startMs, 1)
  return Math.min(1, Math.max(0, (tMs - b.startMs) / span))
}

/**
 * Graduations HORAIRES de l'axe — au plus `maxTicks` étiquettes, sur des
 * heures pleines (pas de « 23:47 » comme origine d'axe).
 */
export function hourTicks(b: TlBounds, maxTicks = 8): { tMs: number; frac: number }[] {
  const HOUR = 3_600_000
  const spanH = Math.max(1, Math.round((b.endMs - b.startMs) / HOUR))
  const step = Math.max(1, Math.ceil(spanH / maxTicks)) * HOUR
  const first = Math.ceil(b.startMs / HOUR) * HOUR
  const out: { tMs: number; frac: number }[] = []
  for (let t = first; t <= b.endMs; t += step) out.push({ tMs: t, frac: fracOf(t, b) })
  return out
}

export interface TlPointIn {
  id: string
  t: string
  kind: string
  label: string
  severity: 'NEUTRAL' | 'WARNING' | 'CRITICAL'
}

export interface TlCluster {
  frac: number
  items: TlPointIn[]
}

/** Jalons qui ne se regroupent JAMAIS : les noyer dans un « 3 événements »
 *  ferait disparaître précisément ce qu'on doit voir d'un coup d'œil. */
const NEVER_CLUSTER = new Set(['CLOSING_ANNOUNCEMENT', 'CANCELLED', 'MC_GRAVE_ENTRY'])

/**
 * Regroupement des points trop proches pour être lisibles côte à côte.
 *
 * `minGap` est une fraction de l'axe (≈ la largeur d'un marqueur + son
 * étiquette). Les jalons majeurs restent TOUJOURS individuels ; seuls les
 * événements ordinaires fusionnent. L'ordre d'entrée est préservé — le tri
 * déterministe vient du serveur.
 */
export function clusterPoints(points: TlPointIn[], b: TlBounds, minGap = 0.035): TlCluster[] {
  const out: TlCluster[] = []
  for (const p of points) {
    const frac = fracOf(new Date(p.t).getTime(), b)
    const last = out[out.length - 1]
    const mergeable = last !== undefined
      && frac - last.frac < minGap
      && !NEVER_CLUSTER.has(p.kind)
      && last.items.every((x) => !NEVER_CLUSTER.has(x.kind))
    if (mergeable) last.items.push(p)
    else out.push({ frac, items: [p] })
  }
  return out
}

export interface TlRangeIn {
  id: string
  kind: string
  start: string
  end: string | null
  label: string
  severity: 'NEUTRAL' | 'WARNING' | 'CRITICAL'
}

export interface TlRangeRow extends TlRangeIn {
  startFrac: number
  /** Fin sur l'axe — pour une plage active, la position de Maintenant. */
  endFrac: number
  active: boolean
  row: number
}

/**
 * Empilement des plages sur le minimum de lignes : deux plages qui ne se
 * chevauchent pas partagent une ligne. Déterministe — l'ordre d'entrée
 * (trié serveur) décide qui se place d'abord.
 */
export function stackRanges(ranges: TlRangeIn[], b: TlBounds, nowMs: number): TlRangeRow[] {
  const rowsEnd: number[] = []
  const out: TlRangeRow[] = []
  for (const r of ranges) {
    const startFrac = fracOf(new Date(r.start).getTime(), b)
    const active = r.end === null
    const endFrac = active ? fracOf(nowMs, b) : fracOf(new Date(r.end!).getTime(), b)
    let row = rowsEnd.findIndex((endF) => startFrac >= endF + 0.008)
    if (row === -1) { row = rowsEnd.length; rowsEnd.push(0) }
    rowsEnd[row] = Math.max(endFrac, startFrac + 0.004)
    out.push({ ...r, startFrac, endFrac: Math.max(endFrac, startFrac + 0.004), active, row })
  }
  return out
}

export interface TlDensityBucket { t: string; scans: number; cumulative: number; par?: Record<string, number> }

export interface TlBar {
  index: number
  /** Coordonnées en unités de viewBox. */
  x: number
  w: number
  y: number
  h: number
  scans: number
  cumulative: number
  t: string
}

/**
 * MICRO-BARRES — une barre par intervalle réellement mesuré.
 *
 * L'aire continue précédente donnait de gros plateaux et suggérait une
 * information continue que nous n'avons pas : la source est DISCRÈTE (un
 * nombre de scans par intervalle). Une barre par intervalle dit exactement
 * cela, sans interpolation ni courbe décorative.
 *
 * Un intervalle sans scan produit une barre de hauteur ZÉRO — il disparaît
 * visuellement, ce qui est la vérité : rien n'a été scanné. Aucun lissage
 * ne vient relier deux zones actives par-dessus le creux.
 */
export function densityBars(
  buckets: TlDensityBucket[], b: TlBounds, stepS: number, w: number, h: number,
): { bars: TlBar[]; max: number } {
  if (buckets.length === 0) return { bars: [], max: 0 }
  const max = Math.max(1, ...buckets.map((x) => x.scans))
  const spanMs = Math.max(b.endMs - b.startMs, 1)
  // Largeur nominale d'un intervalle, moins un filet de respiration. Sous
  // 1,2 unité, les barres se toucheraient : on garde alors le pas plein.
  const nominal = (stepS * 1000 / spanMs) * w
  const gap = nominal > 3 ? Math.min(1.2, nominal * 0.22) : 0
  const bars: TlBar[] = []
  for (const [index, bk] of buckets.entries()) {
    const t0 = new Date(bk.t).getTime()
    const bh = bk.scans === 0 ? 0 : Math.max(1, (bk.scans / max) * h)
    bars.push({
      index, t: bk.t, scans: bk.scans, cumulative: bk.cumulative,
      x: fracOf(t0, b) * w, w: Math.max(0.6, nominal - gap),
      y: h - bh, h: bh,
    })
  }
  return { bars, max }
}

/**
 * TOUTE LA NUIT (19/10/2026) : une barre de densité découpée par billetterie,
 * de bas en haut dans l'ordre donné (Shotgun puis Pretix). La hauteur totale
 * est celle de la barre — la somme des couches est le total de la tranche.
 */
export interface TlCouche { key: string; y: number; h: number }

export function couchesDeLaBarre(bar: Pick<TlBar, 'y' | 'h'>, par: Readonly<Record<string, number>> | undefined,
  ordre: readonly string[]): TlCouche[] {
  if (!par || bar.h <= 0) return []
  const total = ordre.reduce((a, k) => a + (par[k] ?? 0), 0)
  if (total <= 0) return []
  const out: TlCouche[] = []
  let bas = bar.y + bar.h
  for (const k of ordre) {
    const n = par[k] ?? 0
    if (n <= 0) continue
    const h = (n / total) * bar.h
    out.push({ key: k, y: bas - h, h })
    bas -= h
  }
  return out
}

/** « 4 h 12 » — durée compacte pour le résumé. */
export function dureeCourte(ms: number): string {
  const h = Math.floor(ms / 3_600_000)
  const m = Math.round((ms % 3_600_000) / 60_000)
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`
}

export interface TlSummaryIn {
  phase: string
  boundsStartMs: number
  scansTotal: number
  peakT: string | null
  lastScanAt: string | null
  alertRanges: { severity: string; end: string | null }[]
}

/**
 * La ligne de résumé — dérivée des vraies données, phrase par phrase.
 *
 * INVARIANT : on parle de BILLETS SCANNÉS, jamais de personnes présentes —
 * les sorties ne sont pas comptabilisées.
 */
export function buildSummary(s: TlSummaryIn, nowMs: number, formatHeure: (iso: string) => string): string[] {
  const parts: string[] = []
  if (s.phase === 'ONGOING') parts.push(`Nuit en cours · ${dureeCourte(nowMs - s.boundsStartMs)} écoulées`)
  else if (s.phase === 'FINISHED') parts.push('Nuit terminée')
  else if (s.phase === 'CANCELLED') parts.push('Soirée annulée')
  else parts.push('Nuit à venir')

  parts.push(`${s.scansTotal} billet${s.scansTotal > 1 ? 's' : ''} scanné${s.scansTotal > 1 ? 's' : ''}`)
  if (s.peakT) parts.push(`pic ${formatHeure(s.peakT)}`)

  const actives = s.alertRanges.filter((a) => a.end === null)
  const critiques = s.alertRanges.filter((a) => a.severity === 'CRITICAL')
  if (s.alertRanges.length === 0) {
    if (s.phase === 'ONGOING') parts.push('aucune alerte')
  } else if (actives.length > 0) {
    parts.push(`${actives.length} alerte${actives.length > 1 ? 's' : ''} active${actives.length > 1 ? 's' : ''}`)
  } else {
    parts.push(`${s.alertRanges.length} alerte${s.alertRanges.length > 1 ? 's' : ''} résolue${s.alertRanges.length > 1 ? 's' : ''}`)
  }
  if (critiques.length === 0 && s.alertRanges.length > 0) parts.push('aucune critique')

  if (s.phase === 'FINISHED' && s.lastScanAt) parts.push(`dernier scan ${formatHeure(s.lastScanAt)}`)
  return parts
}
